"use client"

import React, { useState, useEffect } from "react"
import { useParams } from "react-router-dom"
import { useRestaurantAuth } from "../../contexts/RestaurantAuthContext"
import { useLanguage } from "../../contexts/LanguageContext"
import { restaurantPortalAPI, restaurantAuthAPI } from "../../services/restaurantApiClient"
import {
  User,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Save,
  Camera,
  Edit3,
  Shield,
  Clock,
  Store,
  MapPin,
  Phone,
  DollarSign,
  Truck,
  Globe,
  Moon,
  Sun
} from "lucide-react"
import { Box, Flex, Grid, Text, TextField, TextArea, Select, Button } from "@radix-ui/themes"
import { toast } from "react-hot-toast"
import { useTheme } from "../../contexts/ThemeContext"
import PageHeader from "../../components/ui/dashboard/PageHeader"
import DataCard from "../../components/ui/dashboard/DataCard"
import LoadingSpinner from "../../components/ui/dashboard/LoadingSpinner"
import ImageUpload from "../../components/ImageUpload"
import { sudanStates, sudanCitiesByState } from "../../utils/sudanLocations"

const RestaurantProfilePage: React.FC = () => {
  const { restaurantId } = useParams()
  const { user } = useRestaurantAuth()
  const { t, language, dir, setLanguage } = useLanguage()
  const { isDarkMode, toggleTheme } = useTheme()
  const [isEditing, setIsEditing] = useState(false)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [fetchingRestaurant, setFetchingRestaurant] = useState(true)
  const [restaurantData, setRestaurantData] = useState<any>(null)

  const [profileData, setProfileData] = useState({
    // Owner Information
    ownerName: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    bio: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",

    // Restaurant Information
    restaurantName: "",
    description: "",
    address: "",
    city: "",
    state: "",
    restaurantEmail: "",
    category: "",
    minimumOrder: 0,
  })

  useEffect(() => {
    const fetchRestaurantData = async () => {
      if (restaurantId) {
        try {
          setFetchingRestaurant(true)
          const response = await restaurantPortalAPI.getRestaurantInfo(restaurantId)
          const restaurant = response.data?.restaurant || response.data
          setRestaurantData(restaurant)
          console.log("Restaurant data:", restaurant) // Debug: check what fields are available

          // Populate form with restaurant data
          setProfileData(prev => ({
            ...prev,
            // Owner Information (from user context)
            ownerName: user?.name || "",
            email: user?.email || "",
            phone: restaurant?.contact?.phone || user?.phone || "",

            // Restaurant Information (from API)
            restaurantName: restaurant?.name || "",
            description: restaurant?.description || "",
            address: restaurant?.address?.street || "",
            city: restaurant?.address?.city || "",
            state: restaurant?.address?.state || "",
            restaurantEmail: restaurant?.contact?.email || user?.email || "",
            minimumOrder: restaurant?.deliveryInfo?.minimumOrder || 0,
            category: restaurant?.category || "",
          }))
        } catch (error) {
          console.error("Error fetching restaurant data:", error)
          toast.error(t('failedToLoadRestaurantData'))
        } finally {
          setFetchingRestaurant(false)
        }
      }
    }
    fetchRestaurantData()
  }, [restaurantId, t, user])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setProfileData(prev => ({ ...prev, [name]: value }))
  }

  const handleNumberInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setProfileData(prev => ({ ...prev, [name]: parseFloat(value) || 0 }))
  }

  const handleSaveProfile = async () => {
    if (!restaurantId) return

    setLoading(true)
    try {
      // Prepare restaurant update data
      const updateData = {
        name: profileData.restaurantName,
        description: profileData.description,
        address: {
          street: profileData.address,
          city: profileData.city,
          state: profileData.state,
          coordinates: restaurantData?.address?.coordinates || {
            latitude: 0,
            longitude: 0
          }
        },
        contact: {
          phone: profileData.phone,
          email: profileData.restaurantEmail,
        },
        deliveryInfo: {
          minimumOrder: profileData.minimumOrder,
        },
        category: profileData.category || restaurantData?.category,
      }

      const response = await restaurantPortalAPI.updateRestaurantInfo(restaurantId, updateData)
      const updatedRestaurant = response.data?.restaurant || response.data
      setRestaurantData(updatedRestaurant)
      toast.success(t('profileUpdatedSuccessfully'))
      setIsEditing(false)
    } catch (error: any) {
      console.error("Error updating restaurant:", error)
      toast.error(error?.response?.data?.message || t('profileUpdateFailed'))
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordChange = async () => {
    if (profileData.newPassword !== profileData.confirmPassword) {
      toast.error(t('passwordsDoNotMatch'))
      return
    }

    if (profileData.newPassword.length < 6) {
      toast.error(t('passwordMinLength'))
      return
    }

    setLoading(true)
    try {
      await restaurantAuthAPI.changePassword({
        currentPassword: profileData.currentPassword,
        newPassword: profileData.newPassword,
      })
      toast.success(t('passwordChangedSuccessfully'))
      setProfileData(prev => ({
        ...prev,
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      }))
    } catch (error: any) {
      console.error("Error changing password:", error)
      toast.error(error?.response?.data?.message || t('passwordChangeFailed'))
    } finally {
      setLoading(false)
    }
  }

  if (fetchingRestaurant) {
    return <LoadingSpinner size="3" text={t("loadingProfileData")} />
  }

  const restaurantImage = restaurantData?.image || restaurantData?.logo || user?.restaurant?.logo

  return (
    <div className="space-y-6" dir={dir}>
      <PageHeader
        title={t("profile")}
        subtitle={t("manageRestaurantProfile")}
      >
        <Button
          onClick={() => setIsEditing(!isEditing)}
          variant={isEditing ? "outline" : "solid"}
          size="3"
          className={`${language === 'ar' ? 'flex-row-reverse' : ''}`}
        >
          <Edit3 className={`w-4 h-4 ${language === 'ar' ? 'ml-2' : 'mr-2'}`} />
          {isEditing ? t('cancel') : t('edit')}
        </Button>
      </PageHeader>

      {/* Profile Header with Logo */}
      <DataCard title={t("restaurantProfile")} subtitle={user?.restaurant?.name}>
        <div className={`flex flex-col md:flex-row items-center ${language === 'ar' ? 'md:flex-row-reverse' : ''} gap-6`}>
          <div className="relative">
            {restaurantImage ? (
              <img
                src={restaurantImage}
                alt={user?.restaurant?.name || "Restaurant"}
                className="w-24 h-24 rounded-full object-cover border-4 border-gray-200 dark:border-gray-700"
                onError={(e) => {
                  // Fallback to initial if image fails to load
                  const target = e.target as HTMLImageElement
                  target.style.display = 'none'
                  const fallback = target.nextElementSibling as HTMLElement
                  if (fallback) fallback.style.display = 'flex'
                }}
              />
            ) : null}
            <div
              className={`w-24 h-24 bg-red-600 rounded-full flex items-center justify-center ${restaurantImage ? 'hidden' : ''}`}
              style={{ display: restaurantImage ? 'none' : 'flex' }}
            >
              <span className="text-3xl font-bold text-white">
                {user?.restaurant?.name?.charAt(0).toUpperCase() || "R"}
              </span>
            </div>
            {isEditing && (
              <div className={`absolute bottom-0 ${language === 'ar' ? 'left-0' : 'right-0'}`}>
                <ImageUpload
                  currentImage={restaurantImage}
                  onImageUploaded={(url) => {
                    setRestaurantData((prev: any) => ({
                      ...prev,
                      image: url,
                      logo: url
                    }))
                    // Update the restaurant in the database
                    if (restaurantId) {
                      restaurantPortalAPI.updateRestaurantInfo(restaurantId, { image: url })
                        .then(() => toast.success(t('logoUpdatedSuccessfully')))
                        .catch(() => toast.error(t('logoUpdateFailed')))
                    }
                  }}
                  uploadEndpoint={`restaurant/${restaurantId}/logo`}
                  entityId={restaurantId}
                  label=""
                  maxSize={5}
                  className="w-auto"
                />
              </div>
            )}
          </div>
          <div className={`flex-1 text-center md:text-left ${language === 'ar' ? 'md:text-right' : ''}`}>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {user?.restaurant?.name}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {user?.restaurant?.address?.street}, {user?.restaurant?.address?.city}
            </p>
          </div>
        </div>
      </DataCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Owner Information */}
        <DataCard
          title={
            <div className={`flex items-center ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
              <User className={`w-5 h-5 text-blue-600 ${language === 'ar' ? 'ml-2' : 'mr-2'}`} />
              <span>{t('ownerInformation')}</span>
            </div>
          }
        >
          <Box>
            <Text size="2" weight="medium" mb="1" as="label" htmlFor="ownerName">
              {t('fullName')}
            </Text>
            <TextField.Root
              id="ownerName"
              name="ownerName"
              value={profileData.ownerName}
              onChange={handleInputChange}
              disabled={!isEditing}
              size="3"
            />
          </Box>
          <Box>
            <Text size="2" weight="medium" mb="1" as="label" htmlFor="email">
              {t('emailAddress')}
            </Text>
            <TextField.Root
              id="email"
              name="email"
              type="email"
              value={profileData.email}
              onChange={handleInputChange}
              disabled={!isEditing}
              size="3"
            >
              <TextField.Slot side={language === 'ar' ? 'right' : 'left'}>
                <Mail className="w-4 h-4" />
              </TextField.Slot>
            </TextField.Root>
          </Box>
          <Box>
            <Text size="2" weight="medium" mb="1" as="label" htmlFor="phone">
              {t('phoneNumber')}
            </Text>
            <TextField.Root
              id="phone"
              name="phone"
              value={profileData.phone}
              onChange={handleInputChange}
              disabled={!isEditing}
              size="3"
            >
              <TextField.Slot side={language === 'ar' ? 'right' : 'left'}>
                <Phone className="w-4 h-4" />
              </TextField.Slot>
            </TextField.Root>
          </Box>
          <Box>
            <Text size="2" weight="medium" mb="1" as="label" htmlFor="bio">
              {t('bio')}
            </Text>
            <TextArea
              id="bio"
              name="bio"
              value={profileData.bio}
              onChange={handleInputChange}
              disabled={!isEditing}
              placeholder={t('tellUsAboutYourself')}
              rows={3}
              size="3"
            />
          </Box>
        </DataCard>

        {/* Security Settings */}
        <DataCard
          title={
            <div className={`flex items-center ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
              <Shield className={`w-5 h-5 text-green-600 ${language === 'ar' ? 'ml-2' : 'mr-2'}`} />
              <span>{t('securitySettings')}</span>
            </div>
          }
        >
          <Box>
            <Text size="2" weight="medium" mb="1" as="label" htmlFor="currentPassword">
              {t('currentPassword')}
            </Text>
            <TextField.Root
              id="currentPassword"
              name="currentPassword"
              type={showCurrentPassword ? "text" : "password"}
              value={profileData.currentPassword}
              onChange={handleInputChange}
              placeholder={t('enterCurrentPassword')}
              size="3"
            >
              <TextField.Slot side={language === 'ar' ? 'right' : 'left'}>
                <Lock className="w-4 h-4" />
              </TextField.Slot>
              <TextField.Slot side={language === 'ar' ? 'left' : 'right'}>
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </TextField.Slot>
            </TextField.Root>
          </Box>
          <Box>
            <Text size="2" weight="medium" mb="1" as="label" htmlFor="newPassword">
              {t('newPassword')}
            </Text>
            <TextField.Root
              id="newPassword"
              name="newPassword"
              type={showNewPassword ? "text" : "password"}
              value={profileData.newPassword}
              onChange={handleInputChange}
              placeholder={t('enterNewPassword')}
              size="3"
            >
              <TextField.Slot side={language === 'ar' ? 'right' : 'left'}>
                <Lock className="w-4 h-4" />
              </TextField.Slot>
              <TextField.Slot side={language === 'ar' ? 'left' : 'right'}>
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </TextField.Slot>
            </TextField.Root>
          </Box>
          <Box>
            <Text size="2" weight="medium" mb="1" as="label" htmlFor="confirmPassword">
              {t('confirmPassword')}
            </Text>
            <TextField.Root
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              value={profileData.confirmPassword}
              onChange={handleInputChange}
              placeholder={t('confirmNewPassword')}
              size="3"
            />
          </Box>
          <Button
            onClick={handlePasswordChange}
            disabled={loading || !profileData.currentPassword || !profileData.newPassword}
            variant="outline"
            size="3"
            className="w-full"
          >
            <Lock className="w-4 h-4" />
            {loading ? t('updating') : t('changePassword')}
          </Button>
        </DataCard>
      </div>

      {/* Restaurant Information */}
      <DataCard
        title={
          <div className={`flex items-center ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
            <Store className={`w-5 h-5 text-red-600 ${language === 'ar' ? 'ml-2' : 'mr-2'}`} />
            <span>{t('restaurantInformation')}</span>
          </div>
        }
      >
        <Grid columns={{ initial: "1", md: "2" }} gap="4">
          <Box>
            <Text size="2" weight="medium" mb="1" as="label" htmlFor="restaurantName">
              {t('restaurantName')}
            </Text>
            <TextField.Root
              id="restaurantName"
              name="restaurantName"
              value={profileData.restaurantName}
              onChange={handleInputChange}
              disabled={!isEditing}
              size="3"
            />
          </Box>
          <Box>
            <Text size="2" weight="medium" mb="1" as="label" htmlFor="city">
              {t('city')}
            </Text>
            <Select.Root
              value={profileData.city}
              onValueChange={(val) => setProfileData(prev => ({ ...prev, city: val }))}
              disabled={!isEditing || !profileData.state}
            >
              <Select.Trigger placeholder={t('selectCity')} className="w-full" />
              <Select.Content>
                {(sudanCitiesByState[profileData.state] || []).map((city) => (
                  <Select.Item key={city.value} value={city.value}>
                    {language === 'ar' ? city.labelAr : city.labelEn}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </Box>
          <Box>
            <Text size="2" weight="medium" mb="1" as="label" htmlFor="state">
              {t('state')}
            </Text>
            <Select.Root
              value={profileData.state}
              onValueChange={(val) => setProfileData(prev => ({ ...prev, state: val, city: "" }))}
              disabled={!isEditing}
            >
              <Select.Trigger placeholder={t('selectState')} className="w-full" />
              <Select.Content>
                {sudanStates.map((state) => (
                  <Select.Item key={state.value} value={state.value}>
                    {language === 'ar' ? state.labelAr : state.labelEn}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </Box>
          {/* Removed ZipCode */}
          <Box>
            <Text size="2" weight="medium" mb="1" as="label" htmlFor="category">
              {t('category')}
            </Text>
            <Select.Root
              value={profileData.category || undefined}
              onValueChange={(value) => setProfileData(prev => ({ ...prev, category: value }))}
              disabled={!isEditing}
            >
              <Select.Trigger style={{ width: "100%" }} placeholder={t('selectCategory')} />
              <Select.Content>
                <Select.Item value="Fast food">{t('fastFood')}</Select.Item>
                <Select.Item value="Pizza">{t('pizza')}</Select.Item>
                <Select.Item value="Asian">{t('asian')}</Select.Item>
                <Select.Item value="Middle-eastern">{t('middleEastern')}</Select.Item>
                <Select.Item value="Desserts">{t('desserts')}</Select.Item>
                <Select.Item value="Healthy">{t('healthy')}</Select.Item>
                <Select.Item value="Coffee">{t('coffee')}</Select.Item>
              </Select.Content>
            </Select.Root>
          </Box>
          <Box style={{ gridColumn: "span 2" }}>
            <Text size="2" weight="medium" mb="1" as="label" htmlFor="description">
              {t('description')}
            </Text>
            <TextArea
              id="description"
              name="description"
              value={profileData.description}
              onChange={handleInputChange}
              disabled={!isEditing}
              rows={3}
              size="3"
            />
          </Box>
          <Box style={{ gridColumn: "span 2" }}>
            <Text size="2" weight="medium" mb="1" as="label" htmlFor="address">
              {t('address')}
            </Text>
            <TextField.Root
              id="address"
              name="address"
              value={profileData.address}
              onChange={handleInputChange}
              disabled={!isEditing}
              size="3"
            >
              <TextField.Slot side={language === 'ar' ? 'right' : 'left'}>
                <MapPin className="w-4 h-4" />
              </TextField.Slot>
            </TextField.Root>
          </Box>
          <Box>
            <Text size="2" weight="medium" mb="1" as="label" htmlFor="restaurantEmail">
              {t('emailAddress')}
            </Text>
            <TextField.Root
              id="restaurantEmail"
              name="restaurantEmail"
              type="email"
              value={profileData.restaurantEmail}
              onChange={handleInputChange}
              disabled={!isEditing}
              size="3"
            >
              <TextField.Slot side={language === 'ar' ? 'right' : 'left'}>
                <Mail className="w-4 h-4" />
              </TextField.Slot>
            </TextField.Root>
          </Box>
          <Box>
            <Text size="2" weight="medium" mb="1" as="label" htmlFor="phone">
              {t('phoneNumber')}
            </Text>
            <TextField.Root
              id="phone"
              name="phone"
              value={profileData.phone}
              onChange={handleInputChange}
              disabled={!isEditing}
              size="3"
            >
              <TextField.Slot side={language === 'ar' ? 'right' : 'left'}>
                <Phone className="w-4 h-4" />
              </TextField.Slot>
            </TextField.Root>
          </Box>
          {/* Removed DeliveryFee */}
          <Box>
            <Text size="2" weight="medium" mb="1" as="label" htmlFor="minimumOrder">
              {t('minimumOrder')}
            </Text>
            <TextField.Root
              id="minimumOrder"
              name="minimumOrder"
              type="number"
              step="0.01"
              value={profileData.minimumOrder}
              onChange={handleNumberInputChange}
              disabled={!isEditing}
              size="3"
            >
              <TextField.Slot side={language === 'ar' ? 'right' : 'left'}>
                <DollarSign className="w-4 h-4" />
              </TextField.Slot>
            </TextField.Root>
          </Box>
          {/* Removed EstimatedTime and DeliveryRadius */}
        </Grid>
        {isEditing && (
          <Button
            onClick={handleSaveProfile}
            disabled={loading}
            size="3"
            className="w-full mt-6"
          >
            <Save className="w-4 h-4" />
            {loading ? t('saving') : t('saveChanges')}
          </Button>
        )}
      </DataCard>

      {/* Account Information */}
      <DataCard
        title={
          <div className={`flex items-center ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
            <Truck className={`w-5 h-5 text-purple-600 ${language === 'ar' ? 'ml-2' : 'mr-2'}`} />
            <span>{t('accountInformation')}</span>
          </div>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className={`flex items-center ${language === 'ar' ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
            <Clock className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{t('memberSince')}</p>
              <p className="text-sm text-gray-500">
                {t('notAvailable')}
              </p>
            </div>
          </div>
          <div className={`flex items-center ${language === 'ar' ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
            <Store className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{t('accountType')}</p>
              <p className="text-sm text-gray-500">{t('restaurantOwner')}</p>
            </div>
          </div>
        </div>
      </DataCard>

      {/* Appearance & Preferences Section */}
      <DataCard
        title={t("appearancePreferences")}
        subtitle={t("customizeYourInterfacePreferences")}
      >
        <Text size="2" color="gray" mb="4">
          {t("appearancePreferencesDescription")}
        </Text>

        <Grid columns={{ initial: "1", md: "2" }} gap="4">
          {/* Language Preference */}
          <Box style={{ backgroundColor: 'var(--gray-3)', padding: '16px', borderRadius: 'var(--radius-3)' }}>
            <Flex direction="column" gap="3">
              <Flex align="center" gap="3" mb="2" style={{ flexDirection: language === 'ar' ? 'row-reverse' : 'row' }}>
                <Globe className="h-5 w-5" style={{ color: 'var(--gray-11)' }} />
                <Box>
                  <Text size="2" weight="medium" mb="1">
                    {t("language")}
                  </Text>
                  <Text size="1" color="gray">
                    {t("selectYourPreferredLanguage")}
                  </Text>
                </Box>
              </Flex>
              <Flex gap="2" style={{ flexDirection: language === 'ar' ? 'row-reverse' : 'row' }}>
                <Button
                  variant={language === 'en' ? 'solid' : 'outline'}
                  color={language === 'en' ? 'red' : 'gray'}
                  size="2"
                  onClick={() => setLanguage('en')}
                >
                  {t("english")}
                </Button>
                <Button
                  variant={language === 'ar' ? 'solid' : 'outline'}
                  color={language === 'ar' ? 'red' : 'gray'}
                  size="2"
                  onClick={() => setLanguage('ar')}
                >
                  {t("arabic")}
                </Button>
              </Flex>
            </Flex>
          </Box>

          {/* Theme Preference */}
          <Box style={{ backgroundColor: 'var(--gray-3)', padding: '16px', borderRadius: 'var(--radius-3)' }}>
            <Flex direction="column" gap="3">
              <Flex align="center" gap="3" mb="2" style={{ flexDirection: language === 'ar' ? 'row-reverse' : 'row' }}>
                {isDarkMode ? (
                  <Sun className="h-5 w-5" style={{ color: 'var(--gray-11)' }} />
                ) : (
                  <Moon className="h-5 w-5" style={{ color: 'var(--gray-11)' }} />
                )}
                <Box>
                  <Text size="2" weight="medium" mb="1">
                    {t("theme")}
                  </Text>
                  <Text size="1" color="gray">
                    {t("switchBetweenLightAndDarkMode")}
                  </Text>
                </Box>
              </Flex>
              <Flex gap="2" style={{ flexDirection: language === 'ar' ? 'row-reverse' : 'row' }}>
                <Button
                  variant={!isDarkMode ? 'solid' : 'outline'}
                  color={!isDarkMode ? 'red' : 'gray'}
                  size="2"
                  onClick={() => !isDarkMode || toggleTheme()}
                >
                  <Sun className="h-4 w-4" style={{ marginRight: language === 'ar' ? '0' : '8px', marginLeft: language === 'ar' ? '8px' : '0' }} />
                  {t("light")}
                </Button>
                <Button
                  variant={isDarkMode ? 'solid' : 'outline'}
                  color={isDarkMode ? 'red' : 'gray'}
                  size="2"
                  onClick={() => isDarkMode || toggleTheme()}
                >
                  <Moon className="h-4 w-4" style={{ marginRight: language === 'ar' ? '0' : '8px', marginLeft: language === 'ar' ? '8px' : '0' }} />
                  {t("dark")}
                </Button>
              </Flex>
            </Flex>
          </Box>
        </Grid>
      </DataCard>
    </div>
  )
}

export default RestaurantProfilePage
